import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getAllVehicleChecks, approveVehicleCheck, rejectVehicleCheck } from '../services/vehicleCheckService';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Search, Download, FileText, Filter, ChevronDown, ChevronUp, CheckCircle, XCircle, AlertTriangle, Clock } from 'lucide-react';

// ─── helpers ────────────────────────────────────────────────────────────────

const today = () => new Date().toISOString().split('T')[0];

const conditionBadge = (val) => {
  if (val === 'satisfactory') return { label: '✓', cls: 'bg-green-100 text-green-700 font-bold' };
  if (val === 'defective')    return { label: '✗', cls: 'bg-red-100 text-red-700 font-bold' };
  return { label: 'N/A', cls: 'bg-gray-100 text-gray-500' };
};

const statusBadge = (status) => {
  if (status === 'approved') return { label: 'Approved', cls: 'bg-green-100 text-green-700', Icon: CheckCircle };
  if (status === 'rejected') return { label: 'Rejected', cls: 'bg-red-100 text-red-700', Icon: XCircle };
  return { label: 'Pending', cls: 'bg-yellow-100 text-yellow-700', Icon: Clock };
};

const fmt = (v) => v || '—';

// All check sections matching the Excel format
const SECTIONS = [
  {
    title: 'REG DOC',
    items: [
      { label: 'Odometer reading', cond: null, value: (c) => fmt(c.odometerReading) + (c.odometerReading ? ' km' : '') },
      { label: 'Last service mileage & date', cond: null, value: (c) => [c.lastServiceMileage ? c.lastServiceMileage + ' km' : null, c.lastServiceDate].filter(Boolean).join(' / ') || '—' },
      { label: 'Next service mileage', cond: null, value: (c) => fmt(c.nextServiceMileage) + (c.nextServiceMileage ? ' km' : '') },
      { label: 'Control technique date', cond: null, value: (c) => fmt(c.controlTechniqueDate) },
      { label: 'Insurance expiration date', cond: null, value: (c) => fmt(c.insuranceExpirationDate) },
      { label: 'Fuel level', cond: null, value: (c) => fmt(c.fuelLevel) + (c.fuelLevel ? '%' : '') },
    ]
  },
  {
    title: 'External Vehicle Condition',
    items: [
      { label: 'Vehicle bodywork, windscreen, windows, lights', cond: 'externalBodyworkCondition', comment: 'externalBodyworkComment' },
      { label: 'Windscreen wiper blades', cond: 'externalWiperBladesCondition', comment: 'externalWiperBladesComment' },
      { label: 'Cleanness of windscreen, windows, mirrors, lights, number plate', cond: 'externalCleannessCondition', comment: 'externalCleannessComment' },
      { label: 'Tyres, tyre pressure, tyre wear', cond: 'externalTyresCondition', comment: 'externalTyresComment' },
    ]
  },
  {
    title: 'Vehicle Interior and Equipment',
    items: [
      { label: 'Seat belts', cond: 'interiorSeatBeltsCondition', comment: 'interiorSeatBeltsComment' },
      { label: 'First aid kit', cond: 'interiorFirstAidKitCondition', comment: 'interiorFirstAidKitComment' },
      { label: 'Fire extinguisher', cond: 'interiorFireExtinguisherCondition', comment: 'interiorFireExtinguisherComment' },
      { label: 'Torch', cond: 'interiorTorchCondition', comment: 'interiorTorchComment' },
      { label: 'Warning triangle', cond: 'interiorWarningTriangleCondition', comment: 'interiorWarningTriangleComment' },
      { label: 'Vehicle log book', cond: 'interiorLogBookCondition', comment: 'interiorLogBookComment' },
      { label: 'Spare tyre & jack', cond: 'interiorSpareTyreCondition', comment: 'interiorSpareTyreComment' },
    ]
  },
  {
    title: 'Fluids',
    items: [
      { label: 'Engine oil level', cond: 'fluidsEngineOilCondition', comment: 'fluidsEngineOilComment' },
      { label: 'Coolant level', cond: 'fluidsCoolantCondition', comment: 'fluidsCoolantComment' },
      { label: 'Windscreen wash level', cond: 'fluidsWindscreenWashCondition', comment: 'fluidsWindscreenWashComment' },
      { label: 'Brake/clutch fluid', cond: 'fluidsBrakeClutchCondition', comment: 'fluidsBrakeClutchComment' },
      { label: 'Power steering fluid', cond: 'fluidsPowerSteeringCondition', comment: 'fluidsPowerSteeringComment' },
      { label: 'Condition of battery', cond: 'fluidsBatteryCondition', comment: 'fluidsBatteryComment' },
      { label: 'Oil or waste leaks', cond: 'fluidsLeaksCondition', comment: 'fluidsLeaksComment' },
    ]
  },
  {
    title: 'Function Checks Before Starting the Journey',
    items: [
      { label: 'Warning light', cond: 'functionWarningLightCondition', comment: 'functionWarningLightComment' },
      { label: 'All lights', cond: 'functionAllLightsCondition', comment: 'functionAllLightsComment' },
      { label: 'Horn', cond: 'functionHornCondition', comment: 'functionHornComment' },
      { label: 'Washers & wipers', cond: 'functionWashersWipersCondition', comment: 'functionWashersWipersComment' },
      { label: 'Brake', cond: 'functionBrakeCondition', comment: 'functionBrakeComment' },
      { label: 'Fuel', cond: 'functionFuelCondition', comment: 'functionFuelComment' },
    ]
  }
];

// ─── PDF export ──────────────────────────────────────────────────────────────

const exportPDF = (checks) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  checks.forEach((check, idx) => {
    if (idx > 0) doc.addPage();

    // Header
    doc.setFillColor(37, 99, 235);
    doc.rect(0, 0, 297, 20, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('DAILY VEHICLE CHECK REPORT', 148, 13, { align: 'center' });

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');

    // Vehicle info box
    autoTable(doc, {
      startY: 24,
      head: [['Vehicle', 'Driver', 'Date', 'Odometer', 'Fuel Level', 'Status']],
      body: [[
        `${check.vehiclePlate || ''} ${check.vehicleInfo || ''}`.trim(),
        check.driverName || '—',
        check.checkDate || '—',
        check.odometerReading ? check.odometerReading + ' km' : '—',
        check.fuelLevel ? check.fuelLevel + '%' : '—',
        (check.status || 'pending').toUpperCase()
      ]],
      headStyles: { fillColor: [59, 130, 246], fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      margin: { left: 10, right: 10 },
    });

    // Check items
    const rows = [];
    SECTIONS.forEach((section) => {
      rows.push([{ content: section.title, colSpan: 3, styles: { fillColor: [243, 244, 246], fontStyle: 'bold', fontSize: 9 } }]);
      section.items.forEach((item) => {
        if (item.cond) {
          const val = check[item.cond] || 'satisfactory';
          const condLabel = val === 'satisfactory' ? '✓ Satisfactory' : val === 'defective' ? '✗ Defective' : 'N/A';
          rows.push([item.label, condLabel, check[item.comment] || '']);
        } else {
          rows.push([item.label, { content: item.value(check), colSpan: 2 }]);
        }
      });
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 4,
      head: [['Item to Check', 'Check', 'Comments']],
      body: rows,
      headStyles: { fillColor: [37, 99, 235], fontSize: 9 },
      bodyStyles: { fontSize: 8 },
      columnStyles: { 0: { cellWidth: 110 }, 1: { cellWidth: 40 }, 2: { cellWidth: 'auto' } },
      margin: { left: 10, right: 10 },
      didParseCell: (data) => {
        if (data.cell.raw && typeof data.cell.raw === 'object' && data.cell.raw.colSpan === 3) return;
        if (data.column.index === 1 && data.section === 'body') {
          const txt = data.cell.text[0] || '';
          if (txt.startsWith('✗')) data.cell.styles.textColor = [220, 38, 38];
          else if (txt.startsWith('✓')) data.cell.styles.textColor = [22, 163, 74];
        }
      }
    });

    if (check.vehicleNotes) {
      const y = doc.lastAutoTable.finalY + 4;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('Notes: ', 10, y + 4);
      doc.setFont('helvetica', 'normal');
      doc.text(check.vehicleNotes, 28, y + 4);
    }
  });

  const dateStr = new Date().toISOString().split('T')[0];
  doc.save(`vehicle-check-report-${dateStr}.pdf`);
};

// ─── Excel export ─────────────────────────────────────────────────────────────

const exportExcel = (checks) => {
  const wb = XLSX.utils.book_new();

  // Summary sheet
  const summaryRows = [
    ['VEHICLE CHECK REPORT', '', '', '', '', '', ''],
    ['Generated:', new Date().toLocaleString(), '', '', '', '', ''],
    [''],
    ['Vehicle Plate', 'Vehicle Info', 'Driver', 'Date', 'Odometer (km)', 'Fuel (%)', 'Status', 'Defects Found']
  ];

  checks.forEach((c) => {
    const defects = SECTIONS.flatMap(s => s.items.filter(i => i.cond && c[i.cond] === 'defective').map(i => i.label));
    summaryRows.push([
      c.vehiclePlate || '',
      c.vehicleInfo || '',
      c.driverName || '',
      c.checkDate || '',
      c.odometerReading || '',
      c.fuelLevel || '',
      (c.status || 'pending').toUpperCase(),
      defects.join(', ') || 'None'
    ]);
  });

  const ws = XLSX.utils.aoa_to_sheet(summaryRows);
  ws['!cols'] = [20, 25, 20, 12, 15, 10, 12, 50].map(w => ({ wch: w }));
  XLSX.utils.book_append_sheet(wb, ws, 'Summary');

  // Detailed sheet per check
  checks.forEach((check, idx) => {
    const rows = [
      ['DAILY VEHICLE CHECK'],
      ['Vehicle:', `${check.vehiclePlate || ''} — ${check.vehicleInfo || ''}`.trim()],
      ['Driver:', check.driverName || ''],
      ['Date:', check.checkDate || ''],
      ['Status:', (check.status || 'pending').toUpperCase()],
      [''],
      ['VEHICLE', 'TO CHECK', 'Check', 'Comments'],
    ];

    SECTIONS.forEach((section) => {
      rows.push([section.title, '', '', '']);
      section.items.forEach((item) => {
        if (item.cond) {
          const val = check[item.cond] || 'satisfactory';
          rows.push(['', item.label, val === 'satisfactory' ? '✓' : val === 'defective' ? '✗' : 'N/A', check[item.comment] || '']);
        } else {
          rows.push(['', item.label, item.value(check), '']);
        }
      });
    });

    if (check.vehicleNotes) {
      rows.push(['']);
      rows.push(['Notes:', check.vehicleNotes]);
    }

    const detailWs = XLSX.utils.aoa_to_sheet(rows);
    detailWs['!cols'] = [20, 50, 15, 30].map(w => ({ wch: w }));
    const sheetName = `${check.vehiclePlate || 'Check'}-${idx + 1}`.replace(/[^a-zA-Z0-9-]/g, '').substring(0, 31);
    XLSX.utils.book_append_sheet(wb, detailWs, sheetName);
  });

  const dateStr = new Date().toISOString().split('T')[0];
  XLSX.writeFile(wb, `vehicle-check-report-${dateStr}.xlsx`);
};

// ─── CheckDetailModal ────────────────────────────────────────────────────────

const CheckDetailModal = ({ check, onClose, onReview }) => {
  const [reviewNote, setReviewNote] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleApprove = async () => {
    setProcessing(true);
    try {
      await approveVehicleCheck(check.id, reviewNote);
      onReview(check.id, 'approved', reviewNote);
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!reviewNote.trim()) {
      alert('Please enter a reason for rejection.');
      return;
    }
    setProcessing(true);
    try {
      await rejectVehicleCheck(check.id, reviewNote, check);
      onReview(check.id, 'rejected', reviewNote);
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setProcessing(false);
    }
  };

  const { label: statusLabel, cls: statusCls, Icon: StatusIcon } = statusBadge(check.status);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-4">
        {/* Modal header */}
        <div className="bg-blue-600 text-white px-6 py-4 rounded-t-xl flex justify-between items-start">
          <div>
            <h2 className="text-xl font-bold">Vehicle Check Report</h2>
            <p className="text-blue-100 text-sm mt-0.5">{check.vehiclePlate} — {check.vehicleInfo}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-white ${statusCls.replace('bg-', 'text-').split(' ')[1]}`}>
              <StatusIcon className="w-3 h-3" />{statusLabel}
            </span>
            <button onClick={onClose} className="text-white hover:text-blue-200 text-xl font-bold leading-none">×</button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Top info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 rounded-lg p-4 text-sm">
            <div><p className="text-gray-500 text-xs">Driver</p><p className="font-semibold">{check.driverName}</p></div>
            <div><p className="text-gray-500 text-xs">Date</p><p className="font-semibold">{check.checkDate}</p></div>
            <div><p className="text-gray-500 text-xs">Odometer</p><p className="font-semibold">{check.odometerReading ? check.odometerReading + ' km' : '—'}</p></div>
            <div><p className="text-gray-500 text-xs">Fuel Level</p><p className="font-semibold">{check.fuelLevel ? check.fuelLevel + '%' : '—'}</p></div>
            <div><p className="text-gray-500 text-xs">Last Service</p><p className="font-semibold">{[check.lastServiceMileage ? check.lastServiceMileage + ' km' : null, check.lastServiceDate].filter(Boolean).join(' / ') || '—'}</p></div>
            <div><p className="text-gray-500 text-xs">Next Service</p><p className="font-semibold">{check.nextServiceMileage ? check.nextServiceMileage + ' km' : '—'}</p></div>
            <div>
              <p className="text-gray-500 text-xs">Remaining to Service</p>
              {(() => {
                const remaining = check.nextServiceMileage && check.odometerReading
                  ? Number(check.nextServiceMileage) - Number(check.odometerReading)
                  : null;
                if (remaining === null) return <p className="font-semibold text-gray-400">—</p>;
                if (remaining <= 0) return <p className="font-semibold text-red-600">Overdue by {Math.abs(remaining)} km</p>;
                if (remaining <= 500) return <p className="font-semibold text-orange-500">{remaining} km left</p>;
                return <p className="font-semibold text-green-600">{remaining} km left</p>;
              })()}
            </div>
            <div><p className="text-gray-500 text-xs">Control Technique</p><p className="font-semibold">{check.controlTechniqueDate || '—'}</p></div>
            <div><p className="text-gray-500 text-xs">Insurance Expiry</p><p className="font-semibold">{check.insuranceExpirationDate || '—'}</p></div>
          </div>

          {/* Check sections */}
          {SECTIONS.slice(1).map((section) => (
            <div key={section.title}>
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-2 border-b pb-1">{section.title}</h3>
              <div className="space-y-1">
                {section.items.map((item) => {
                  const badge = conditionBadge(check[item.cond]);
                  const comment = check[item.comment];
                  return (
                    <div key={item.label} className="flex items-start justify-between text-sm py-1">
                      <span className="text-gray-700 flex-1 pr-4">{item.label}</span>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`px-2 py-0.5 rounded text-xs ${badge.cls} whitespace-nowrap`}>{badge.label}</span>
                        {comment && <span className="text-gray-500 text-xs italic truncate max-w-[160px]" title={comment}>{comment}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {check.vehicleNotes && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
              <span className="font-semibold text-yellow-800">Notes: </span>
              <span className="text-yellow-700">{check.vehicleNotes}</span>
            </div>
          )}

          {check.reviewNote && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <span className="font-semibold text-blue-800">Review Note: </span>
              <span className="text-blue-700">{check.reviewNote}</span>
            </div>
          )}

          {/* Approve / Reject */}
          {check.status === 'pending' && (
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <p className="text-sm font-semibold text-gray-700">Review this check</p>
              <textarea
                value={reviewNote}
                onChange={e => setReviewNote(e.target.value)}
                placeholder="Add a review note (required for rejection)..."
                rows={2}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleApprove}
                  disabled={processing}
                  className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
                >
                  <CheckCircle className="w-4 h-4" /> Approve
                </button>
                <button
                  onClick={handleReject}
                  disabled={processing}
                  className="flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50"
                >
                  <XCircle className="w-4 h-4" /> Reject
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2 border-t">
            <button onClick={() => exportExcel([check])} className="flex items-center gap-2 px-4 py-2 border border-green-600 text-green-700 rounded-lg hover:bg-green-50 text-sm font-medium">
              <Download className="w-4 h-4" /> Excel
            </button>
            <button onClick={() => exportPDF([check])} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
              <FileText className="w-4 h-4" /> PDF
            </button>
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 text-sm font-medium">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

const Reports = () => {
  const [checks, setChecks] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCheck, setSelectedCheck] = useState(null);

  // Filters
  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(today());
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [checksData, vehiclesSnap] = await Promise.all([
        getAllVehicleChecks(null),
        getDocs(query(collection(db, 'vehicles'), orderBy('registration_no')))
      ]);
      setChecks(checksData);
      setVehicles(vehiclesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error loading report data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = checks.filter((c) => {
    if (dateFrom && c.checkDate < dateFrom) return false;
    if (dateTo && c.checkDate > dateTo) return false;
    if (vehicleFilter !== 'all' && c.vehicleId !== vehicleFilter) return false;
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      if (
        !c.driverName?.toLowerCase().includes(term) &&
        !c.vehiclePlate?.toLowerCase().includes(term) &&
        !c.vehicleInfo?.toLowerCase().includes(term)
      ) return false;
    }
    return true;
  });

  const defectCount = (c) =>
    SECTIONS.flatMap(s => s.items.filter(i => i.cond)).filter(i => c[i.cond] === 'defective').length;

  const stats = {
    total: filtered.length,
    approved: filtered.filter(c => c.status === 'approved').length,
    pending: filtered.filter(c => c.status === 'pending').length,
    withDefects: filtered.filter(c => defectCount(c) > 0).length,
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vehicle Check Reports</h1>
          <p className="text-gray-500 text-sm mt-0.5">Daily vehicle inspection records</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => exportExcel(filtered)}
            disabled={filtered.length === 0}
            className="flex items-center gap-2 px-4 py-2 border border-green-600 text-green-700 rounded-lg hover:bg-green-50 text-sm font-medium disabled:opacity-40"
          >
            <Download className="w-4 h-4" /> Excel
          </button>
          <button
            onClick={() => exportPDF(filtered)}
            disabled={filtered.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-40"
          >
            <FileText className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Checks', value: stats.total, cls: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Approved', value: stats.approved, cls: 'text-green-600', bg: 'bg-green-50' },
          { label: 'Pending Review', value: stats.pending, cls: 'text-yellow-600', bg: 'bg-yellow-50' },
          { label: 'With Defects', value: stats.withDefects, cls: 'text-red-600', bg: 'bg-red-50' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4`}>
            <p className="text-xs text-gray-500 mb-1">{s.label}</p>
            <p className={`text-3xl font-bold ${s.cls}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search driver or vehicle..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {/* Date from */}
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <span className="self-center text-gray-400 text-sm hidden sm:block">to</span>
          {/* Date to */}
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {/* More filters toggle */}
          <button
            onClick={() => setShowFilters(p => !p)}
            className="flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
          >
            <Filter className="w-4 h-4" />
            Filters
            {showFilters ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {/* Today shortcut */}
          <button
            onClick={() => { setDateFrom(today()); setDateTo(today()); }}
            className="px-3 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-sm font-medium hover:bg-blue-100"
          >
            Today
          </button>
        </div>

        {showFilters && (
          <div className="flex flex-col sm:flex-row gap-3 mt-3 pt-3 border-t border-gray-100">
            <select
              value={vehicleFilter}
              onChange={e => setVehicleFilter(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Vehicles</option>
              {vehicles.map(v => (
                <option key={v.id} value={v.id}>{v.registration_no} — {v.make} {v.model}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <button
              onClick={() => { setVehicleFilter('all'); setStatusFilter('all'); setSearchTerm(''); setDateFrom(today()); setDateTo(today()); }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-3 text-gray-500">Loading reports...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No checks found</p>
            <p className="text-gray-400 text-sm mt-1">Try adjusting the filters or date range</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-gray-600 font-semibold">Date</th>
                  <th className="text-left px-4 py-3 text-gray-600 font-semibold">Vehicle</th>
                  <th className="text-left px-4 py-3 text-gray-600 font-semibold">Driver</th>
                  <th className="text-left px-4 py-3 text-gray-600 font-semibold">Odometer</th>
                  <th className="text-left px-4 py-3 text-gray-600 font-semibold">To Next Service</th>
                  <th className="text-left px-4 py-3 text-gray-600 font-semibold">Fuel</th>
                  <th className="text-left px-4 py-3 text-gray-600 font-semibold">Defects</th>
                  <th className="text-left px-4 py-3 text-gray-600 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 text-gray-600 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((check) => {
                  const dc = defectCount(check);
                  const { label: sl, cls: sc, Icon: SIcon } = statusBadge(check.status);
                  return (
                    <tr key={check.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{check.checkDate || '—'}</td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-900">{check.vehiclePlate}</p>
                        <p className="text-gray-500 text-xs">{check.vehicleInfo}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{check.driverName}</td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{check.odometerReading ? check.odometerReading + ' km' : '—'}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {(() => {
                          const remaining = check.nextServiceMileage && check.odometerReading
                            ? Number(check.nextServiceMileage) - Number(check.odometerReading)
                            : null;
                          if (remaining === null) return <span className="text-gray-400">—</span>;
                          if (remaining <= 0) return <span className="text-red-600 font-semibold">Overdue ({Math.abs(remaining)} km)</span>;
                          if (remaining <= 500) return <span className="text-orange-500 font-semibold">{remaining} km</span>;
                          return <span className="text-green-600">{remaining} km</span>;
                        })()}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{check.fuelLevel ? check.fuelLevel + '%' : '—'}</td>
                      <td className="px-4 py-3">
                        {dc > 0 ? (
                          <span className="flex items-center gap-1 text-red-600 font-semibold">
                            <AlertTriangle className="w-3.5 h-3.5" />{dc}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="w-3.5 h-3.5" />None
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold w-fit ${sc}`}>
                          <SIcon className="w-3 h-3" />{sl}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setSelectedCheck(check)}
                          className="px-3 py-1 text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 text-xs font-medium"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selectedCheck && (
        <CheckDetailModal
          check={selectedCheck}
          onClose={() => setSelectedCheck(null)}
          onReview={(id, status, note) => {
            setChecks(prev => prev.map(c => c.id === id ? { ...c, status, reviewNote: note } : c));
          }}
        />
      )}
    </div>
  );
};

export default Reports;
